import type { CityConfig } from "../city-template/types";

export const modestoConfig: CityConfig = {
  city: "Modesto",
  state: "California",
  stateAbbr: "CA",
  slug: "modesto",
  route: "/cities/modesto",
  title: "Emergency Passport Modesto, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Modesto, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Stanislaus County and Central Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Modesto, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Modesto and Central Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Modesto%20California%20cityscape%20at%20golden%20hour%20with%20downtown%20skyline%2C%20modern%20buildings%20along%20the%20Tuolumne%20River%2C%20lush%20green%20trees%20and%20agricultural%20landscape%20in%20the%20background%2C%20warm%20sunset%20light%20casting%20amber%20tones%20over%20the%20Central%20Valley%2C%20clean%20modern%20urban%20environment%20with%20wide%20streets%2C%20professional%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20urban%20environment&width=1800&height=700&seq=modesto-hero-001&orientation=landscape",
  heroImageAlt: "Modesto California skyline at golden hour with downtown buildings and Tuolumne River landscape",
  geoPosition: "37.6391;-120.9969",
  geoPlacename: "Modesto, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, San Francisco, CA 94102",
    distance: "Located in San Francisco — approximately 90 minutes from Modesto via I-580",
  },
  nearbyCitiesZips: [
    { name: "Modesto", zips: "95350–95358, 95397" },
    { name: "Turlock", zips: "95380, 95382" },
    { name: "Ceres", zips: "95307" },
    { name: "Manteca", zips: "95336, 95337" },
    { name: "Ripon", zips: "95366" },
    { name: "Oakdale", zips: "95361" },
    { name: "Riverbank", zips: "95367" },
    { name: "Salida", zips: "95368" },
    { name: "Hughson", zips: "95326" },
    { name: "Newman", zips: "95360" },
    { name: "Patterson", zips: "95363" },
    { name: "Stockton", zips: "95201–95212" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Central Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "Stockton", route: "/cities/stockton" },
    { name: "Fresno", route: "/cities/fresno" },
    { name: "Bakersfield", route: "/cities/bakersfield" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Modesto remotely via secure courier. We do not have a walk-in office in Modesto — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Central Valley travelers.",
  fpcOfficeDistance: "Serving all of Stanislaus County",
  serviceAreaIntro: "From downtown Modesto to the suburbs of Salida and Riverbank, from Ceres to the north and Oakdale to the east — Fast Passport Center serves the entire Stanislaus County community. We regularly help travelers from Turlock, Manteca, Patterson, and beyond get their passports fast.",
  nearbyCitiesIntro: "We serve all Modesto neighborhoods and nearby Central Valley cities including Turlock, Ceres, Manteca, Ripon, Oakdale, and Stockton. If you are in Stanislaus County or nearby San Joaquin County, we have you covered.",
  howItWorksIntro: "Most Modesto travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With Modesto being a hub for Central Valley agriculture and business travelers, passport delays can derail important trips. Here are the most common mistakes we see.",
  testimonialsIntro: "Modesto and Central Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Modesto? Here are the most common ones we hear from Central Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Central Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Carlos M.",
      location: "Modesto, CA",
      rating: 5,
      text: "My business trip to Mexico was in 2 days and my passport had expired. Fast Passport Center got me a new one in 30 hours. Absolutely saved my business deal.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa H.",
      location: "Turlock, CA",
      rating: 5,
      text: "Needed to renew my passport for a family cruise to the Caribbean. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Robert K.",
      location: "Ceres, CA",
      rating: 5,
      text: "Lost my passport the week before my daughter's wedding in Hawaii. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Amanda T.",
      location: "Manteca, CA",
      rating: 5,
      text: "As a frequent international traveler for work from the Central Valley, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "modesto-1",
      name: "Modesto Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "915 11th St",
      city: "Modesto",
      state: "CA",
      zip: "95354",
      phone: "(209) 524-3340",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Modesto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "modesto-2",
      name: "Turlock Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "555 E Main St",
      city: "Turlock",
      state: "CA",
      zip: "95380",
      phone: "(209) 632-4221",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Turlock",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "modesto-3",
      name: "Ceres Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2800 5th St",
      city: "Ceres",
      state: "CA",
      zip: "95307",
      phone: "(209) 537-2224",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Ceres",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for southern Stanislaus County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "modesto-4",
      name: "Manteca Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "211 E Yosemite Ave",
      city: "Manteca",
      state: "CA",
      zip: "95336",
      phone: "(209) 823-3557",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Manteca",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern San Joaquin County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "modesto-5",
      name: "Stockton Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4040 E Main St",
      city: "Stockton",
      state: "CA",
      zip: "95205",
      phone: "(209) 944-6350",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Stockton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for San Joaquin County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "modesto-6",
      name: "Stanislaus County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1021 I St",
      city: "Modesto",
      state: "CA",
      zip: "95354",
      phone: "(209) 525-5250",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.2 miles from Modesto City Hall",
      website: "https://www.stancounty.com/clerk/",
      websiteLabel: "Stanislaus County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Central Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Stanislaus County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Modesto applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Central Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Central Valley travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Modesto." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Modesto address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Modesto before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Modesto for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Central Valley travelers." },
    { q: "How fast can I renew my passport in Modesto?", a: "Fast Passport Center offers fast passport renewal in Modesto in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my trip?", a: "If your passport expires before your trip, contact Fast Passport Center immediately. We can expedite your passport renewal in Modesto in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Modesto?", a: "Same-day passport appointments require a long drive to the nearest regional agency. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Modesto residents." },
    { q: "Can I expedite a child passport in Modesto?", a: "Yes. Fast Passport Center expedites child passports in Modesto for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Modesto with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Modesto?",
      a: "With Fast Passport Center's emergency courier service, Modesto travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Modesto?",
      a: "Popular options include the Modesto Main Post Office (95354), Turlock Post Office (95380), Ceres Post Office (95307), and Stanislaus County Clerk (95354). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Modesto?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Turlock or Manteca?",
      a: "We serve all of Stanislaus County and nearby San Joaquin County including Turlock, Ceres, Manteca, Ripon, Oakdale, and Stockton. Our overnight shipping labels work from any UPS location, so your location within the Central Valley doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Modesto?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Modesto?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Modesto and Turlock offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Modesto?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Modesto?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Central Valley families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Modesto", address: "2012 W Briggsmore Ave, Modesto, CA 95350", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Turlock", address: "1440 W Main St, Turlock, CA 95380", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Modesto", address: "3801 Pelandale Ave, Modesto, CA 95356", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Manteca", address: "1237 W Lathrop Rd, Manteca, CA 95336", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Ceres", address: "1630 E Hatch Rd, Ceres, CA 95307", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Modesto", address: "1900 Oakdale Rd, Modesto, CA 95355", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Ripon", address: "1231 W Main St, Ripon, CA 95366", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Modesto", address: "3801 Pelandale Ave, Modesto, CA 95356", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Modesto",
      address: "1240 W Orangeburg Ave, Modesto, CA 95350",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Turlock",
      address: "1200 W Main St, Turlock, CA 95380",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Manteca",
      address: "955 S Main St, Manteca, CA 95337",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Stockton",
      address: "2027 E Mariposa Rd, Stockton, CA 95205",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for San Joaquin County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "MOD",
      name: "Modesto City–County Airport",
      distance: "3 miles from downtown Modesto",
      address: "617 Airport Way, Modesto, CA 95354",
      note: "Regional airport with general aviation and limited commercial service. Closest commercial hub is Sacramento or San Jose.",
      flightUrl: "https://www.modestoairport.com/",
      flightLabel: "Modesto Airport",
      featured: true,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "75 miles from Modesto",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "Major international airport with flights to Mexico, Canada, and Asia. Accessible via CA-99 and I-680.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: false,
    },
    {
      code: "SAC",
      name: "Sacramento International Airport",
      distance: "80 miles from Modesto",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "International airport serving Northern California with flights to Europe, Mexico, and Hawaii. Accessible via I-5.",
      flightUrl: "https://www.sacramento.aero/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
};